import { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { Box, Cyl, Ball, Caps } from "./Box";
import { Interactive } from "./Interactive";
import { ArtFrame } from "./Refs";
import { CustomModel } from "./CustomObjects";
import { REGISTRY, viewFor } from "./registry";
import { useContent, useSection } from "@/content/ContentContext";

const TERRA = "#C85A32";
const SAGE = "#6E8B74";
const CREAM = "#EFE3CF";
const GOLD = "#D9A441";
const INK = "#1E1E22";
const SKIN = "#E3B08C";

const Man = () => {
  const arm = useRef();
  useFrame(({ clock }) => {
    if (arm.current) arm.current.rotation.z = 2.5 + Math.sin(clock.elapsedTime * 3) * 0.25;
  });
  return (
    <group>
      <Box p={[0, 0.55, 0]} s={[0.62, 0.08, 0.6]} c="#3A2C24" t="leather" r={0.55} />
      <Box p={[0, 1.0, -0.3]} s={[0.6, 0.75, 0.07]} c="#3A2C24" t="leather" r={0.55} />
      <Cyl p={[0, 0.3, 0]} a={[0.04, 0.04, 0.5, 10]} c="#2B2A2E" m={0.5} />
      {[0, 1, 2, 3, 4].map((i) => (
        <Box key={i} p={[Math.cos(i * 1.256) * 0.18, 0.05, Math.sin(i * 1.256) * 0.18]} rot={[0, -i * 1.256, 0]} s={[0.36, 0.04, 0.05]} c="#2B2A2E" />
      ))}
      <Caps p={[0, 0.95, -0.05]} a={[0.18, 0.3]} c={TERRA} />
      <Box p={[0, 1.12, 0.1]} s={[0.12, 0.08, 0.04]} c={CREAM} />
      <Ball p={[0, 1.38, -0.03]} rad={0.155} c={SKIN} t="plastic" />
      <mesh position={[0, 1.42, -0.03]} castShadow>
        <sphereGeometry args={[0.165, 24, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color="#2F3A33" roughness={1} />
      </mesh>
      <Ball p={[0, 1.6, -0.03]} rad={0.045} c={TERRA} />
      <Box p={[0, 1.38, 0.14]} s={[0.22, 0.045, 0.02]} c={INK} />
      <Caps p={[0.1, 0.66, 0.15]} a={[0.075, 0.26]} rot={[Math.PI / 2, 0, 0]} c="#2E3440" />
      <Caps p={[-0.1, 0.66, 0.15]} a={[0.075, 0.26]} rot={[Math.PI / 2, 0, 0]} c="#2E3440" />
      <Caps p={[0.1, 0.36, 0.33]} a={[0.065, 0.26]} c="#2E3440" />
      <Caps p={[-0.1, 0.36, 0.33]} a={[0.065, 0.26]} c="#2E3440" />
      <Box p={[0.1, 0.06, 0.39]} s={[0.13, 0.08, 0.24]} c={CREAM} />
      <Box p={[-0.1, 0.06, 0.39]} s={[0.13, 0.08, 0.24]} c={CREAM} />
      <Caps p={[-0.25, 0.98, 0]} a={[0.06, 0.26]} rot={[0, 0, -0.15]} c={TERRA} />
      <Caps p={[-0.25, 0.78, 0.15]} a={[0.055, 0.2]} rot={[Math.PI / 2, 0, 0]} c={SKIN} t="plastic" />
      <group ref={arm} position={[0.24, 1.13, 0]}>
        <Caps p={[0, -0.22, 0]} a={[0.06, 0.3]} c={TERRA} />
        <Ball p={[0, -0.43, 0]} rad={0.06} c={SKIN} t="plastic" />
      </group>
    </group>
  );
};

const FilmCamera = () => {
  const r1 = useRef();
  const r2 = useRef();
  useFrame((_, dt) => {
    if (r1.current) r1.current.rotation.x += dt * 1.2;
    if (r2.current) r2.current.rotation.x += dt * 1.2;
  });
  return (
    <group>
      <Box p={[0, 0.03, 0]} s={[0.3, 0.06, 0.5]} c="#2B2A2E" />
      <Box p={[0, 0.22, 0]} s={[0.26, 0.3, 0.48]} c={INK} r={0.5} t="leather" round={0.02} />
      <Cyl p={[0, 0.22, 0.33]} rot={[Math.PI / 2, 0, 0]} a={[0.09, 0.1, 0.2, 24]} c="#111" r={0.3} />
      <Cyl p={[0, 0.22, 0.3]} rot={[Math.PI / 2, 0, 0]} a={[0.105, 0.105, 0.03, 24]} c={GOLD} m={0.7} r={0.3} />
      <Box p={[0, 0.22, 0.46]} s={[0.32, 0.26, 0.05]} c="#111" />
      <Box p={[0, 0.4, -0.02]} s={[0.06, 0.05, 0.3]} c="#2B2A2E" />
      <Box p={[0.16, 0.34, -0.05]} s={[0.02, 0.13, 0.2]} c="#2B2A2E" />
      <Box p={[0.172, 0.34, -0.05]} s={[0.005, 0.11, 0.18]} c="#334" e="#6EA0C8" ei={0.9} shadow={false} />
      <Ball p={[-0.1, 0.39, 0.2]} rad={0.02} c="#ff3b2f" e="#ff3b2f" ei={2} />
      <group ref={r1} position={[0, 0.58, -0.14]}>
        <Cyl a={[0.15, 0.15, 0.035, 28]} rot={[0, 0, Math.PI / 2]} c="#3A3A40" m={0.6} r={0.4} />
        <Box p={[0, 0, 0]} s={[0.04, 0.26, 0.03]} c="#5A5A62" />
      </group>
      <group ref={r2} position={[0, 0.55, 0.15]}>
        <Cyl a={[0.12, 0.12, 0.035, 28]} rot={[0, 0, Math.PI / 2]} c="#3A3A40" m={0.6} r={0.4} />
        <Box p={[0, 0, 0]} s={[0.04, 0.2, 0.03]} c="#5A5A62" />
      </group>
    </group>
  );
};

const PhotoCamera = () => (
  <group>
    <Box p={[0, 0.09, 0]} s={[0.32, 0.17, 0.11]} c="#2B2A2E" r={0.6} t="leather" round={0.015} />
    <Box p={[0, 0.19, 0]} s={[0.32, 0.04, 0.11]} c="#C9C2B6" m={0.6} r={0.35} />
    <Box p={[-0.08, 0.235, 0]} s={[0.09, 0.06, 0.08]} c="#C9C2B6" m={0.6} r={0.35} />
    <Cyl p={[0.03, 0.09, 0.1]} rot={[Math.PI / 2, 0, 0]} a={[0.065, 0.07, 0.12, 24]} c="#151515" r={0.3} />
    <Cyl p={[0.03, 0.09, 0.161]} rot={[Math.PI / 2, 0, 0]} a={[0.048, 0.048, 0.005, 24]} c="#3E5C76" m={0.8} r={0.1} />
    <Cyl p={[0.11, 0.225, 0]} a={[0.018, 0.018, 0.03, 12]} c={TERRA} />
    {[[0.32, 0.4], [0.42, -0.2]].map(([x, r], i) => (
      <group key={i} position={[x, 0.004 + i * 0.006, 0.06]} rotation={[0, r, 0]}>
        <Box s={[0.15, 0.016, 0.18]} c={CREAM} t="paper" round={0.003} />
        <Box p={[0, 0.0085, -0.015]} s={[0.12, 0.002, 0.12]} c={i ? SAGE : "#E69C24"} shadow={false} />
      </group>
    ))}
  </group>
);

const Clapper = ({ hovered }) => {
  const top = useRef();
  useFrame((_, dt) => {
    if (!top.current) return;
    const target = hovered ? -0.5 : 0;
    top.current.rotation.z += (target - top.current.rotation.z) * Math.min(1, dt * 10);
  });
  return (
    <group>
      <group rotation={[-0.28, 0, 0]}>
        <Box p={[0, 0.18, 0]} s={[0.42, 0.34, 0.045]} c={INK} t="wood" round={0.006} />
        {[0.08, 0.14, 0.2].map((y) => (
          <Box key={y} p={[0, y, 0.017]} s={[0.36, 0.008, 0.004]} c={CREAM} shadow={false} />
        ))}
        <group ref={top} position={[-0.21, 0.37, 0]}>
          <Box p={[0.21, 0.03, 0]} s={[0.42, 0.06, 0.035]} c={CREAM} />
          {[0, 1, 2, 3].map((i) => (
            <Box key={i} p={[0.06 + i * 0.105, 0.03, 0.019]} rot={[0, 0, 0.6]} s={[0.04, 0.07, 0.004]} c={INK} shadow={false} />
          ))}
        </group>
      </group>
      <Box p={[-0.36, 0.03, 0.12]} s={[0.24, 0.05, 0.32]} rot={[0, 0.2, 0]} c={CREAM} />
      <Box p={[-0.35, 0.065, 0.12]} s={[0.24, 0.02, 0.32]} rot={[0, -0.1, 0]} c="#E8D6BB" />
    </group>
  );
};

const Diploma = () => (
  <group>
    <Box p={[0, 0, 0.035]} s={[1.0, 0.76, 0.07]} c="#A7782A" m={0.3} r={0.5} t="wood" round={0.01} />
    <Box p={[0, 0, 0.074]} s={[0.86, 0.62, 0.01]} c="#F6EEDC" t="paper" shadow={false} />
    <Box p={[0, 0.2, 0.06]} s={[0.5, 0.04, 0.004]} c="#3D2415" shadow={false} />
    {[0.08, 0.02, -0.04].map((y, i) => (
      <Box key={y} p={[-0.05, y, 0.06]} s={[0.6 - i * 0.12, 0.015, 0.004]} c="#8C8377" shadow={false} />
    ))}
    <Cyl p={[0.28, -0.18, 0.065]} rot={[Math.PI / 2, 0, 0]} a={[0.065, 0.065, 0.012, 24]} c="#B23A2A" />
    <Box p={[0.25, -0.27, 0.062]} rot={[0, 0, 0.3]} s={[0.03, 0.12, 0.004]} c="#B23A2A" shadow={false} />
    <Box p={[0.31, -0.27, 0.062]} rot={[0, 0, -0.3]} s={[0.03, 0.12, 0.004]} c="#B23A2A" shadow={false} />
  </group>
);

const Trophy = () => (
  <group>
    <Box p={[0, 0.05, 0]} s={[0.18, 0.1, 0.18]} c="#3D2415" />
    <Cyl p={[0, 0.16, 0]} a={[0.025, 0.04, 0.14, 12]} c={GOLD} m={0.85} r={0.25} />
    <Cyl p={[0, 0.3, 0]} a={[0.12, 0.05, 0.18, 24]} c={GOLD} m={0.85} r={0.25} />
    <mesh position={[0.12, 0.3, 0]} rotation={[0, 0, 0]}>
      <torusGeometry args={[0.05, 0.012, 8, 16, Math.PI]} />
      <meshStandardMaterial color={GOLD} metalness={0.85} roughness={0.25} />
    </mesh>
    <mesh position={[-0.12, 0.3, 0]} rotation={[0, Math.PI, 0]}>
      <torusGeometry args={[0.05, 0.012, 8, 16, Math.PI]} />
      <meshStandardMaterial color={GOLD} metalness={0.85} roughness={0.25} />
    </mesh>
    <Box p={[0.26, 0.09, 0.02]} rot={[-0.15, -0.3, 0]} s={[0.12, 0.17, 0.01]} c={TERRA} />
    <Box p={[0.26, 0.1, 0.028]} rot={[-0.15, -0.3, 0]} s={[0.08, 0.05, 0.004]} c={CREAM} shadow={false} />
  </group>
);

const Paintings = () => (
  <group>
    <ArtFrame position={[-0.85, 0.1, 0]} w={0.72} h={0.92} art="ombrelle" frame="#B8862E" depth={0.08} mat={false} />
    <ArtFrame position={[0.1, -0.05, 0]} w={0.88} h={0.66} art="soleil" frame="#B8862E" depth={0.08} mat={false} />
    <ArtFrame position={[0.92, 0.22, 0]} w={0.52} h={0.52} art="nympheas" frame="#B8862E" depth={0.08} mat={false} />
  </group>
);

const tube = (pts, r) => new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts.map((p) => new THREE.Vector3(...p))), 24, r, 8, false);

const GlobeStand = () => {
  const geo = useMemo(() => {
    const profile = [[0, 0.5], [0.07, 0.5], [0.075, 0.53], [0.05, 0.56], [0.065, 0.62], [0.045, 0.7], [0.035, 0.8], [0.05, 0.86], [0.07, 0.9], [0.0, 0.92]]
      .map(([x, y]) => new THREE.Vector2(x, y));
    const legs = [0, 1, 2].map((k) => {
      const a = (k / 3) * Math.PI * 2 + 0.5;
      const c = Math.cos(a); const s = Math.sin(a);
      return tube([[c * 0.05, 0.52, s * 0.05], [c * 0.2, 0.42, s * 0.2], [c * 0.3, 0.18, s * 0.3], [c * 0.36, 0.04, s * 0.36]], 0.022);
    });
    const arms = [0, 1, 2].map((k) => {
      const a = (k / 3) * Math.PI * 2 + 0.5 + Math.PI / 3;
      const c = Math.cos(a); const s = Math.sin(a);
      return tube([[c * 0.06, 0.88, s * 0.06], [c * 0.3, 0.95, s * 0.3], [c * 0.47, 1.15, s * 0.47], [c * 0.5, 1.33, s * 0.5]], 0.014);
    });
    return { lathe: new THREE.LatheGeometry(profile, 32), legs, arms };
  }, []);
  return (
    <group>
      <mesh geometry={geo.lathe} castShadow receiveShadow>
        <meshStandardMaterial color="#5C3A21" roughness={0.45} />
      </mesh>
      {[...geo.legs, ...geo.arms].map((g, i) => (
        <mesh key={i} geometry={g} castShadow>
          <meshStandardMaterial color="#5C3A21" roughness={0.45} />
        </mesh>
      ))}
      {[0, 1, 2].map((k) => {
        const a = (k / 3) * Math.PI * 2 + 0.5;
        return <Ball key={k} p={[Math.cos(a) * 0.37, 0.03, Math.sin(a) * 0.37]} rad={0.03} c={GOLD} m={0.8} r={0.3} />;
      })}
      <mesh position={[0, 1.35, 0]} rotation={[Math.PI / 2, 0, 0]} scale={[1, 1, 0.35]} castShadow>
        <torusGeometry args={[0.5, 0.035, 10, 64]} />
        <meshStandardMaterial color="#6E4428" roughness={0.5} map={null} />
      </mesh>
      <mesh position={[0, 1.362, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.47, 0.53, 64]} />
        <meshStandardMaterial color="#EFE3CF" roughness={0.8} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
};

const Globe = () => {
  const g = useRef();
  useFrame((_, dt) => { if (g.current) g.current.rotation.y += dt * 0.25; });
  return (
    <group>
      <GlobeStand />
      <group position={[0, 1.35, 0]} rotation={[0, 0, 0.41]}>
        <mesh>
          <torusGeometry args={[0.44, 0.012, 8, 64]} />
          <meshStandardMaterial color={GOLD} metalness={0.85} roughness={0.25} />
        </mesh>
        <Cyl p={[0, 0.445, 0]} a={[0.012, 0.012, 0.05, 8]} c={GOLD} m={0.85} r={0.25} />
        <Cyl p={[0, -0.445, 0]} a={[0.012, 0.012, 0.05, 8]} c={GOLD} m={0.85} r={0.25} />
        <group ref={g}>
          <Ball rad={0.4} c="#FFFFFF" r={0.35} seg={48} t="globe" />
          {[[0.3, 0.2, 0.18], [-0.2, 0.25, 0.27], [0.1, -0.1, 0.38]].map((p, i) => (
            <Ball key={`pin${i}`} p={p} rad={0.022} c={TERRA} e={TERRA} ei={0.6} />
          ))}
        </group>
      </group>
    </group>
  );
};

const RecordPlayer = () => {
  const disc = useRef();
  useFrame((_, dt) => { if (disc.current) disc.current.rotation.y += dt * 3.5; });
  const music = useSection("music")?.data;
  const records = Array.isArray(music?.records) ? music.records : [];
  return (
    <group>
      <group position={[0.45, 0, 0]}>
        <Box p={[0, 0.06, 0]} s={[0.55, 0.12, 0.62]} c="#8A5A35" t="wood" r={0.5} />
        <Cyl p={[0, 0.13, -0.04]} a={[0.23, 0.23, 0.02, 40]} c="#2B2A2E" />
        <group ref={disc} position={[0, 0.145, -0.04]}>
          <Cyl a={[0.215, 0.215, 0.01, 40]} c="#0E0E10" r={0.3} />
          <Cyl p={[0, 0.006, 0]} a={[0.07, 0.07, 0.004, 24]} c={TERRA} />
          <Box p={[0.04, 0.008, 0]} s={[0.03, 0.002, 0.012]} c={CREAM} shadow={false} />
        </group>
        <Cyl p={[0.18, 0.16, 0.2]} a={[0.025, 0.025, 0.06, 12]} c="#C9C2B6" m={0.6} />
        <Box p={[0.14, 0.2, 0.09]} rot={[0, 0.35, 0]} s={[0.02, 0.015, 0.26]} c="#C9C2B6" m={0.6} />
      </group>
      {records.slice(0, 4).map((r, i) => (
        <Box key={i} p={[-0.3 + i * 0.03, 0.22, -0.3 + i * 0.06]} rot={[-0.1 + i * 0.03, 0, 0]} s={[0.42, 0.42, 0.035]} c={r.color || TERRA} r={0.7} t="paper" round={0.004} />
      ))}
      <Box p={[-0.85, 0.17, -0.05]} s={[0.26, 0.34, 0.3]} c="#3A2C24" t="leather" r={0.55} />
      <Cyl p={[-0.85, 0.18, 0.105]} rot={[Math.PI / 2, 0, 0]} a={[0.08, 0.08, 0.01, 24]} c="#1A1A1A" />
    </group>
  );
};

const PALETTE = ["#C85A32", "#E69C24", "#6E8B74", "#3E5C76", "#E8D6BB", "#8C5A7A", "#2F3A33", "#B0603E"];

const Bookshelf = () => {
  const books = useMemo(() => {
    const out = [];
    let seed = 3;
    const rnd = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
    [0.07, 0.72, 1.37, 2.02].forEach((y, row) => {
      let x = -0.72;
      while (x < 0.62 - row * 0.1) {
        const w = 0.05 + rnd() * 0.05;
        const h = 0.38 + rnd() * 0.18;
        out.push({ p: [x + w / 2, y + h / 2, 0.02], s: [w, h, 0.36], c: PALETTE[Math.floor(rnd() * PALETTE.length)] });
        x += w + 0.008;
      }
    });
    return out;
  }, []);
  return (
    <group>
      <Box p={[0.8, 1.3, 0]} s={[0.05, 2.65, 0.55]} c="#7A4E2E" t="wood" />
      <Box p={[-0.8, 1.3, 0]} s={[0.05, 2.65, 0.55]} c="#7A4E2E" t="wood" />
      <Box p={[0, 1.3, -0.26]} s={[1.6, 2.65, 0.03]} c="#5C3A21" t="wood" />
      {[0.04, 0.69, 1.34, 1.99, 2.62].map((y) => (
        <Box key={y} p={[0, y, 0]} s={[1.6, 0.05, 0.55]} c="#8A5A35" t="wood" />
      ))}
      {books.map((b, i) => <Box key={i} p={b.p} s={b.s} c={b.c} r={0.7} t="leather" round={0.006} />)}
      <Cyl p={[-0.4, 2.75, 0]} rot={[Math.PI / 2, 0, 0]} a={[0.13, 0.13, 0.04, 28]} c="#3A3A40" m={0.5} />
    </group>
  );
};

const Dumbbell = ({ position, rotation }) => (
  <group position={position} rotation={rotation}>
    <Cyl rot={[0, 0, Math.PI / 2]} a={[0.025, 0.025, 0.42, 10]} c="#9A9AA2" m={0.8} r={0.3} />
    {[-0.17, -0.11, 0.11, 0.17].map((x) => (
      <Cyl key={x} p={[x, 0, 0]} rot={[0, 0, Math.PI / 2]} a={[0.1, 0.1, 0.05, 20]} c="#26262B" r={0.5} />
    ))}
  </group>
);

const Sports = () => (
  <group>
    <Box p={[-0.1, 0.018, 0.1]} s={[0.7, 0.035, 1.7]} rot={[0, 0.3, 0]} c={SAGE} r={1} t="rug" tr={[2, 4]} round={0.012} />
    <Cyl p={[0.55, 0.12, -0.6]} rot={[0, 0.3, Math.PI / 2]} a={[0.12, 0.12, 0.7, 20]} c={TERRA} r={1} t="rug" tr={[2, 2]} />
    <Dumbbell position={[-0.1, 0.11, 0.3]} rotation={[0, 0.5, 0]} />
    <Dumbbell position={[0.1, 0.11, -0.05]} rotation={[0, 0.2, 0]} />
    <group position={[-0.45, 0, -0.35]}>
      <Ball p={[0, 0.14, 0]} rad={0.15} c={SAGE} r={0.6} sc={[1, 0.9, 1]} />
      <mesh position={[0, 0.29, 0]}>
        <torusGeometry args={[0.08, 0.025, 8, 20, Math.PI]} />
        <meshStandardMaterial color="#26262B" roughness={0.5} />
      </mesh>
    </group>
  </group>
);

const Contact = () => {
  const hs = useRef();
  useFrame(({ clock }) => {
    if (!hs.current) return;
    const t = clock.elapsedTime % 6;
    hs.current.position.y = 0.2 + (t < 0.7 ? Math.abs(Math.sin(t * 40)) * 0.015 : 0);
  });
  return (
    <group>
      <Cyl p={[0, 0.66, 0]} a={[0.36, 0.36, 0.04, 32]} c="#8A5A35" t="wood" r={0.5} />
      <Cyl p={[0, 0.33, 0]} a={[0.035, 0.035, 0.64, 10]} c="#5C3A21" t="wood" />
      <Cyl p={[0, 0.02, 0]} a={[0.2, 0.22, 0.04, 24]} c="#5C3A21" t="wood" />
      <group position={[0, 0.68, 0.02]} rotation={[0, 0.6, 0]}>
        <Box p={[0, 0.07, 0]} s={[0.3, 0.14, 0.24]} c="#B5442E" r={0.3} t="plastic" round={0.03} />
        <Cyl p={[0, 0.13, 0.09]} rot={[1.1, 0, 0]} a={[0.075, 0.075, 0.02, 24]} c={CREAM} />
        <Cyl p={[0, 0.142, 0.098]} rot={[1.1, 0, 0]} a={[0.025, 0.025, 0.01, 16]} c="#B5442E" />
        <group ref={hs} position={[0, 0.2, -0.04]}>
          <Caps rot={[0, 0, Math.PI / 2]} a={[0.03, 0.24]} c="#B5442E" r={0.3} t="plastic" />
          <Box p={[0.15, -0.03, 0]} s={[0.08, 0.06, 0.09]} c="#B5442E" />
          <Box p={[-0.15, -0.03, 0]} s={[0.08, 0.06, 0.09]} c="#B5442E" />
        </group>
      </group>
      <group position={[-0.12, 0.69, -0.18]} rotation={[0, -0.4, 0]}>
        <Box s={[0.26, 0.022, 0.17]} c={CREAM} t="paper" round={0.004} />
        <Box p={[0.02, 0.022, 0.02]} rot={[0, 0.25, 0]} s={[0.26, 0.022, 0.17]} c="#F6EEDC" t="paper" round={0.004} />
        <Cyl p={[0.03, 0.022, 0.02]} a={[0.025, 0.025, 0.008, 16]} c="#B23A2A" />
      </group>
    </group>
  );
};

const Reel = ({ p, rad }) => (
  <group position={p}>
    <Cyl rot={[Math.PI / 2, 0, 0]} a={[rad, rad, 0.03, 32]} c="#9A9AA2" m={0.8} r={0.3} />
    <Cyl p={[0, 0, 0.002]} rot={[Math.PI / 2, 0, 0]} a={[rad * 0.72, rad * 0.72, 0.034, 32]} c="#2B1E17" r={0.6} />
    <Cyl p={[0, 0, 0.004]} rot={[Math.PI / 2, 0, 0]} a={[rad * 0.22, rad * 0.22, 0.04, 16]} c={GOLD} m={0.8} r={0.3} />
  </group>
);

// Projecteur de cinéma vintage posé sur un tabouret : la rubrique « Crédits »
const Projector = () => (
  <group>
    <Cyl p={[0, 0.3, 0]} a={[0.2, 0.24, 0.6, 24]} c="#5C3A21" t="wood" />
    <Box p={[0, 0.72, 0]} s={[0.34, 0.22, 0.2]} c={INK} m={0.3} r={0.5} round={0.02} />
    <Box p={[0, 0.72, 0.101]} s={[0.26, 0.12, 0.004]} c="#3A3A40" m={0.5} shadow={false} />
    <Cyl p={[0.06, 0.72, 0.15]} rot={[Math.PI / 2, 0, 0]} a={[0.045, 0.055, 0.1, 20]} c="#2A2A2E" m={0.6} r={0.4} />
    <Cyl p={[0.06, 0.72, 0.202]} rot={[Math.PI / 2, 0, 0]} a={[0.036, 0.036, 0.006, 20]} c="#FFD58A" e="#FFB852" ei={2.2} />
    <Box p={[-0.08, 0.92, 0]} s={[0.02, 0.2, 0.02]} c={GOLD} m={0.8} r={0.3} />
    <Box p={[0.1, 0.89, 0]} s={[0.02, 0.15, 0.02]} c={GOLD} m={0.8} r={0.3} />
    <Reel p={[-0.08, 1.06, 0.02]} rad={0.14} />
    <Reel p={[0.1, 0.99, 0.02]} rad={0.1} />
    <Box p={[0.01, 0.9, 0.035]} rot={[0, 0, -0.5]} s={[0.008, 0.2, 0.004]} c="#2B1E17" shadow={false} />
    <Box p={[0.1, 0.62, 0.15]} rot={[-0.3, 0.2, 0.1]} s={[0.12, 0.004, 0.07]} c={CREAM} t="paper" />
  </group>
);

const BUILT_IN = [
  { id: "about", position: [0.4, 0, -2.0], rotation: [0, 0.45, 0], C: Man },
  { id: "films", position: [1.6, 1.04, -3.3], rotation: [0, 0.55, 0], C: FilmCamera },
  { id: "photos", position: [0.75, 1.04, -2.95], rotation: [0, -0.25, 0], C: PhotoCamera },
  { id: "experience", position: [-0.1, 1.04, -3.2], rotation: [0, 0.25, 0], C: Clapper, hover: true },
  { id: "education", position: [-2.4, 2.95, -4.0], C: Diploma },
  { id: "associative", position: [-2.15, 2.03, -3.82], C: Trophy },
  { id: "art", position: [2.2, 3.0, -4.0], C: Paintings },
  { id: "languages", position: [-3.1, 0, -3.1], C: Globe },
  { id: "music", position: [-3.5, 0.8, -0.6], rotation: [0, Math.PI / 2, 0], C: RecordPlayer },
  { id: "skills", position: [-3.65, 0, 2.3], rotation: [0, Math.PI / 2, 0], C: Bookshelf },
  { id: "sports", position: [1.9, 0, 2.4], C: Sports },
  { id: "contact", position: [3.2, 0, -1.4], C: Contact },
  { id: "credits", position: [-1.1, 0, 3.5], rotation: [0, 0.35, 0], C: Projector },
];

export const Objects = ({ ui }) => {
  const { sections } = useContent();
  const byId = Object.fromEntries(sections.map((s) => [s.id, s]));
  const custom = sections.filter((s) => !REGISTRY[s.id] && s.visible !== false);
  return (
    <group>
      {BUILT_IN.map(({ id, position, rotation, C, hover }) => {
        const s = byId[id];
        if (!s || s.visible === false) {
          return <group key={id} position={position} rotation={rotation}><C hovered={false} /></group>;
        }
        return (
          <Interactive key={id} id={id} label={s.label} hotspot={REGISTRY[id].hotspot} position={position} rotation={rotation} ui={ui}>
            {hover ? ({ hovered }) => <C hovered={hovered} /> : <C />}
          </Interactive>
        );
      })}
      {custom.map((s) => {
        const v = viewFor(s);
        return (
          <Interactive key={s.id} id={s.id} label={s.label} hotspot={v.hotspot} position={v.slot} rotation={[0, 0.5, 0]} ui={ui}>
            <CustomModel model={s.model} />
          </Interactive>
        );
      })}
    </group>
  );
};
